import * as moment from 'moment';
import {findRoomService} from "../network/services/find-room.service";
import {getDistance} from "../helpers/gps.helper";
import {DISABLE_ROOM_IF_ON_CART, IF_BAR_THEN_ONLY_BAR, ONLY_SAME_ZONE_BOOKABLE} from "../helpers/config";
import {MAX_PEOPLE_IN_SEARCH, SWITCH_DAY_AFTER_HOUR} from "../common";

export const ROOM_SORTABLE = {
    AREA: 'AREA',
    PRICE: 'PRICE'
}

export default {
    namespaced: true,
    state: {
        availableRoomZones: [],
        rooms: [],
        available_rooms: [],
        roomGroups: [],
        requesting: false,
        selected: null,
        hovered: null,
        filteredByProvider: false,
        filteredByShoppingCart: false,
        resultStart: null,
        resultEnd: null,
        search: {
            query: null,
            start: null,
            end: null,
            travelers: {
                adults: 2,
                children: 0,
                babies: 0
            },
            equipments: [],
            zone: null,
            group: null,
            sort: 'AREA',
            direction: 'DESC',
            isBusiness: false,
            businessCode: null
        },
        mapSelected: [],
        sorters: []
    },
    getters: {
        hasDates: state => state.search.start !== null && state.search.end !== null,
        nbGuests: state => state.search.travelers.adults + state.search.travelers.children,
        roomGroups: state => state.roomGroups.filter((roomGroup) => {
            return state.rooms.map((room) => room.room_group.id).indexOf(roomGroup.id) !== -1;
        }),
        geoRooms: (state, getters) => {
            if (state.requesting) return [];
            return getters.rooms.reduce((o, room) => {
                const roomPosition = room.position;
                for (const index in o) {
                    const value = o[index];
                    if (getDistance(value.position, roomPosition) < 20) {
                        value.rooms.push(room);
                        return o;
                    }
                }
                o.push({
                    position: roomPosition,
                    rooms: [room]
                })
                return o;
            }, [])
        },
        roomList: (state, getters) => {
            return getters.rooms.filter(room => {
                if (state.mapSelected.length === 0) return true;
                return state.mapSelected.indexOf(room.id) !== -1;
            })
        },
        rooms: (state, getters, rootState, rootGetters) => {
            state.filteredByProvider = false;
            state.filteredByShoppingCart = false;
            return state.rooms.map((room) => {
                if (DISABLE_ROOM_IF_ON_CART && rootGetters['shoppingCart/roomIds'].indexOf(room.id) !== -1) {
                    for (const booking of rootGetters["shoppingCart/bookings"]) {
                        for (const booking_room of booking.booking_rooms) {
                            if (booking_room.room.id !== room.id) continue;
                            const lockStart = moment(booking.start).format('YYYY-MM-DD') + ' 17:00:00';
                            const lockEnd = moment(booking.end).format('YYYY-MM-DD') + ' 12:00:00';
                            const start = moment(state.resultStart).format('YYYY-MM-DD') + ' 17:00:00';
                            const end = moment(state.resultEnd).format('YYYY-MM-DD') + ' 12:00:00';

                            if (lockStart <= end && start <= lockEnd) {
                                room.has_priceplans = false;
                                return room;
                            }
                        }
                    }
                }
                room.has_priceplans = true;
                return room;
            }).filter((room) => {
                if (state.search.group && state.search.group.indexOf(room.room_group.id) === -1) return false;
                if (Array.isArray(rootState.equipment.picked) && rootState.equipment.picked.length > 0) {
                    const roomEquipments = room.available_equipments;
                    for (const id of rootState.equipment.picked) {
                        if (roomEquipments.indexOf(id) === -1) return false;
                    }
                }
                return true;
            }).map(room => {
                const pricePlanObj = {};
                if (room.has_priceplans) {
                    for (const pricePlanDate of room.price_plans) {
                        const input = pricePlanDate.input_prices[0]; // guess only first works
                        for (const edge of input.edges) {
                            if (!pricePlanObj[edge.from.id]) {
                                pricePlanObj[edge.from.id] = Object.assign({}, edge);
                                continue;
                            }
                            if (edge.altPrice) {
                                if (!pricePlanObj[edge.from.id].altPrice) {
                                    pricePlanObj[edge.from.id].altPrice = {
                                        extraAdditionalPrice: 0,
                                        hostingPrice: 0,
                                        additionalPrice: 0,
                                        touristTax: 0,
                                        totalPrice: 0,
                                    }
                                }
                                pricePlanObj[edge.from.id].altPrice.extraAdditionalPrice += edge.altPrice.extraAdditionalPrice;
                                pricePlanObj[edge.from.id].altPrice.hostingPrice += edge.altPrice.hostingPrice;
                                pricePlanObj[edge.from.id].altPrice.additionalPrice += edge.altPrice.additionalPrice;
                                pricePlanObj[edge.from.id].altPrice.touristTax += edge.altPrice.touristTax;
                                pricePlanObj[edge.from.id].altPrice.totalPrice += edge.altPrice.totalPrice;
                            }
                            pricePlanObj[edge.from.id].extraAdditionalPrice += edge.extraAdditionalPrice;
                            pricePlanObj[edge.from.id].hostingPrice += edge.hostingPrice;
                            pricePlanObj[edge.from.id].additionalPrice += edge.additionalPrice;
                            pricePlanObj[edge.from.id].touristTax += edge.touristTax;
                            pricePlanObj[edge.from.id].totalPrice += edge.totalPrice;
                        }
                    }
                }
                room.computedPrices = Object.values(pricePlanObj).sort((a, b) => (a.from.display_order < b.from.display_order ? -1 : 1));
                room.computedMinPrice = room.computedPrices.reduce((prev, current) => {
                    if (prev === -1) prev = current.hostingPrice;
                    return Math.min(prev, current.hostingPrice);
                }, -1);
                return room;
            }).sort((a, b) => {
                for (const sorter of state.sorters) {
                    const coef = sorter.direction === 'DESC' ? -1 : 1;
                    if (sorter.sort === ROOM_SORTABLE.AREA) {
                        if (a.area === b.area) continue;
                        return a.area > b.area ? coef : coef * -1;
                    }
                    if (sorter.sort === ROOM_SORTABLE.PRICE) {
                        if (a.computedMinPrice === b.computedMinPrice) continue;
                        return a.computedMinPrice > b.computedMinPrice ? coef : coef * -1;
                    }
                }
                return a.number < b.number ? -1 : 1;
            })
        }
    },
    mutations: {
        SET_MAP_SELECTED (state, payload) {
            state.mapSelected = payload;
        },
        SET_SELECTED (state, payload) {
            state.selected = payload;
        },
        SET_HOVERED (state, payload) {
            state.hovered = payload;
        },
        SET_ROOM_GROUPS (state, data) {
            state.roomGroups = data;
        },
        SET_REQUESTING (state, payload) {
            state.requesting = !!payload;
        },
        SET_ROOMS (state, rooms) {
            state.rooms = rooms;
        },
        SET_AVAILABLE_ROOMS (state, ids) {
            state.available_rooms = ids;
        },
        SET_AVAILABLE_ROOM_ZONES (state, value) {
          state.availableRoomZones = value;
        },
        SET_SEARCH_STRING (state, value) {
            if (value === '') value = null;
            state.search.query = value;
        },
        SET_SEARCH_START_DATE (state, date) {
            state.search.start = date;
        },
        SET_SEARCH_END_DATE (state, date) {
            state.search.end = date;
        },
        SET_SEARCH_TRAVELERS (state, travelers) {
            state.search.travelers = travelers;
        },
        SET_SEARCH_EQUIPMENTS (state, equipments) {
            state.search.equipments = equipments;
        },
        SET_SEARCH_ZONE (state, zone) {
            state.search.zone = zone;
        },
        SET_SEARCH_GROUP (state, group) {
            state.search.group = group;
        },
        ADD_SEARCH_SORTER (state, {sort, direction}) {
            state.sorters = state.sorters.filter((sorter) => sorter.sort !== sort);
            state.sorters.push({sort, direction});
        },
        REMOVE_SEARCH_SORTER (state, {sort}) {
            state.sorters = state.sorters.filter((sorter) => sorter.sort !== sort);
        },
        SET_SEARCH_BUSINESS (state, payload) {
            if (payload === '') payload = null;
            state.search.isBusiness = !!payload;
            state.search.businessCode = payload;
        },
        SET_DATES (state, {start, end}) {
            state.resultStart = start;
            state.resultEnd = end;
        },
        AUTO_FILL (state, payload) {
            if (!state.search.start) {
                state.search.start = moment().toDate();
            }
            if (!state.search.end) {
                state.search.end = moment(state.search.start).add(1, 'd').toDate();
            }
        }
    },
    actions: {
        async process (context, options = {}) {
            if (!context.state.search.start || !context.state.search.end) {
                if (!options.runAuto) {
                    console.error('no date selected');
                    return;
                }
                const isNext = parseInt(moment().format('H')) >= SWITCH_DAY_AFTER_HOUR;
                const current = moment();
                if (isNext) current.add(1, 'day');
                context.commit('SET_SEARCH_START_DATE', current.toDate())
                context.commit('SET_SEARCH_END_DATE', current.add(1, 'day').toDate());
            }
            context.commit('SET_REQUESTING', true);
            const params = {
                lang: context.rootState.lang.currentLang,
                query: context.state.search.query,
                start: moment(context.state.search.start).format('YYYY-MM-DD'),
                end: moment(context.state.search.end).format('YYYY-MM-DD'),
                travelers: {
                    adults: context.state.search.travelers.adults,
                    children: context.state.search.travelers.children,
                    babies: context.state.search.travelers.babies
                },
                equipments: context.state.search.equipments,
                zone: context.state.search.zone,
                group: null,
                businessCode: context.state.search.businessCode,
                isAuth: context.rootGetters.isAuth
            };
            const response = await findRoomService.request(params, context.rootState.client.tokens.accessToken);
            const data = response.get('Room.search', []);
            const available = response.get('Room.available', []);
            const roomGroups = response.get('RoomZone.one.room_groups', []);
            context.commit('SET_DATES', {start: context.state.search.start, end: context.state.search.end});
            if (options.keepGroups !== true) {
                context.commit('SET_SEARCH_GROUP', null);
            }
            context.commit('SET_ROOM_GROUPS', roomGroups);
            context.commit('SET_ROOMS', data);
            context.commit('SET_AVAILABLE_ROOMS', available);
            context.commit('SET_REQUESTING', false);
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/search.store.js